import React, { useEffect, useMemo, useState } from "react";
import { SelectChangeEvent, Stack } from "@mui/material";
import { Account, Currency, Direction, Ticker, TradingViewLaunchProps } from "../../_app/types";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { APP_ID, TIME_FRAME_PRESETS } from "../../_app/consts";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import AutoStrategyPagination from "./components/AutoStrategyPagination";
import AutoStrategyRangeSettings from "./components/AutoStrategyRangeSettings";
import AutoStrategyInvestmentSettings from "./components/AutoStrategyInvestmentSettings";
import AutoStrategyTimeframeSettings from "./components/AutoStrategyTimeframeSettings";
import {
  PositionBehaviour,
  setBehaviour,
  setCustomFrame,
  setStrategyBase,
  setStrategyCurrencyType,
  setStrategyDirection,
  setStrategyInvestment,
  setStrategyLeverage,
  setStrategyMaxPrice,
  setStrategyMinPrice,
  setStrategyStartPrice,
  setStrategyStepsCount,
  setStrategyTicker,
  TimeFrame,
} from "../../store/slices/strategySlice";
import { getMiddlePrice } from "../../utils/utils";
import { precision } from "../../utils/_precision";
import AutoStrategyTimeframe from "./components/AutoStrategyTimeframe/AutoStrategyTimeframe";
import AutoStrategyPreviewPage from "./components/AutoStrategyPreviewPage";
import binanceAPI from "../../class/binanceApi";
import BybitAPI from "../../class/bybit/bybitApi";
import { ChartingLibraryWidgetOptions, LoadingScreenOptions } from "../../charting_library";
import { useTheme } from "@mui/material/styles";
import { TradingView } from "../../components/ui/TradingView";
import AdvancedSettingsStrategy from "./components/AdvancedSettingsStrategy";
import { toggleChart } from "../../store/slices/appSettingsSlice";
import CollapseChildren from "../../_components/CollapseChildren";
import Header from "../../_modules/Header/Header";
import PageLayout from "../../_components/PageLayout";
import Spacer from "../../_components/Spacer";
import { useAppNavigate } from "../../_hooks/use-app-navigate";
import { getLastEntityLeverage } from "../../utils/async";
import { useApi } from "../../_hooks/use-api";
import TickerSelect from "../../_components/TickerSelect/TickerSelect";
import {ButtonStyled} from "../../_components/ButtonStyled";

interface Props {
  ticker?: string;
  tickerPrice: number;
  accounts: Account[];
  tickers: Ticker[];
}

const AutoStrategyCreationPage = ({ accounts, ticker, tickerPrice, tickers }: Props) => {
  const dispatch = useDispatch();
  const api = useApi();
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.user);
  const navigate = useAppNavigate();
  const showChart = useSelector((state: RootState) => state.appSettings.showChart);

  const strategy = useSelector((state: RootState) => state.strategy);
  const activeAccountID = useSelector((state: RootState) => state.user.activeAccount.id);
  const profileId = useSelector((state: RootState) => state.user.profile.profileId);

  const handleChartView = () => {
    dispatch(toggleChart(!showChart));
    localStorage.setItem("viewChart" + APP_ID, String(!showChart));
  };

  const [step, setStep] = useState<number>(1);
  const [isEditing, setIsEditing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [showTimeFrame, setShowTimeFrame] = useState<boolean>(false);
  const [timeTerm, setTimeTerm] = React.useState<"Short" | "Long" | "Mid">("Short");
  const [timeFrames, setTimeFrames] = useState<TimeFrame[]>(TIME_FRAME_PRESETS[timeTerm]);
  const exchange = useSelector((state: RootState) => state.user.activeAccount.market);

  const middlePrice = useMemo(() => {
    return getMiddlePrice(
      strategy.minPrice,
      strategy.maxPrice,
      precision(strategy.ticker.priceStep)
    );
  }, [strategy.minPrice, strategy.maxPrice, strategy.ticker]);

  const ChartOptions: TradingViewLaunchProps = useMemo(() => {
    return {
      locale: "en",
      debug: false,
      symbol: `${strategy.ticker.base}${strategy.ticker.quote}`,
      interval: "15" as ChartingLibraryWidgetOptions["interval"],
      theme: theme.palette.mode === "dark" ? "dark" : "light",
      timezone: Intl.DateTimeFormat().resolvedOptions()
        .timeZone as ChartingLibraryWidgetOptions["timezone"],
      autosize: true,
      datafeed:
        exchange === "BINANCE"
          ? new binanceAPI({
              debug: false,
              ticker: `${strategy.ticker.base}${strategy.ticker.quote}`,
            })
          : new BybitAPI({
              debug: false,
              ticker: `${strategy.ticker.base}${strategy.ticker.quote}`,
            }),
      loading_screen: {
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[500] : theme.palette.grey[300],
      } as LoadingScreenOptions,
    };
  }, [strategy.ticker]);

  const handleDirection = (value: Direction) => dispatch(setStrategyDirection(value));
  const handleStartPrice = (value: number) => dispatch(setStrategyStartPrice(value));
  const handleCurrencyType = (value: Currency) => dispatch(setStrategyCurrencyType(value));
  const handleInvestment = (value: number) => dispatch(setStrategyInvestment(value));
  const handleLeverage = (value: number) => dispatch(setStrategyLeverage(value));
  const handleMinPrice = (value: number) => dispatch(setStrategyMinPrice(value));
  const handleMaxPrice = (value: number) => dispatch(setStrategyMaxPrice(value));
  const handleStepsCount = (value: number) => dispatch(setStrategyStepsCount(value));
  const handleBehaviour = (value: PositionBehaviour) => dispatch(setBehaviour(value));

  function findTickerByBase(tickers: Ticker[], base: string): Ticker {
    return tickers.find((ticker) => ticker.base === base) as Ticker;
  }

  const handleBase = (event: SelectChangeEvent<any>) => {
    event.preventDefault();
    dispatch(setStrategyTicker(findTickerByBase(tickers, event.target.value)));
    dispatch(setStrategyBase(event.target.value));
  };

  const totalPercent = useMemo(() => {
    return timeFrames.reduce((sum, frame) => {
      return sum + (frame.checked ? frame.assignedVolume : 0);
    }, 0);
  }, [timeFrames]);

  const [showPreview, setShowPreview] = useState<boolean>(false);

  useEffect(() => {
    dispatch(setCustomFrame(false));
    setTimeFrames(TIME_FRAME_PRESETS[timeTerm]);
  }, [timeTerm]);

  useEffect(() => {
    if (!profileId) return;
    if (!activeAccountID) return;

    const fetchLeverage = async () => {
      try {
        const result = await getLastEntityLeverage(api, activeAccountID, profileId, "strategies");
        if (result) {
          dispatch(setStrategyLeverage(result as number));
        } else {
          dispatch(setStrategyLeverage(1));
        }
      } catch (error) {
        dispatch(setStrategyLeverage(1));
      }
    };
    fetchLeverage();
  }, [activeAccountID, profileId]);

  return (
    <PageLayout
      contentSx={{ padding: "20px 8px 0" }}
      header={
        <Header
          pageTitle={"Auto strategy creation"}
          needBack
          onChange={() => {
            if (step === 1) {
              navigate.back();
            }
            setStep((prev) => prev - 1);
          }}
        />
      }
      bottomButton={
        <AutoStrategyPagination
          step={step}
          strategy={strategy}
          onChangeStep={setStep}
          showPreview={setShowPreview}
          editing={isEditing}
        />
      }
    >
      <Box>
        <>
          <Stack spacing={1}>
            <Stack direction={"row"} spacing={1}>
              <TickerSelect
                tickers={tickers}
                quote={strategy.quote}
                base={strategy.base}
                onChange={handleBase}
              />
              <ButtonStyled
                style={{
                  maxWidth: 96,
                  padding: "8px 16px",
                }}
                fullWidth={true}
                onClick={() => handleChartView()}
                color={"secondary"}
                variant="contained"
                endIcon={showChart
                  ? <VisibilityIcon />
                  : <VisibilityOffIcon />
                }
              >
                Chart
              </ButtonStyled>
            </Stack>
          </Stack>
          <CollapseChildren show={showChart} collapsedSize={18} hasNextNode>
            <TradingView
              background={theme.palette.background.default}
              headerColor={theme.palette.background.default}
              ChartProperties={ChartOptions}
            />
          </CollapseChildren>
        </>

        {step === 1 && (
          <AutoStrategyRangeSettings
            strategy={strategy}
            onChangeDirection={handleDirection}
            onChangeMinPrice={handleMinPrice}
            onChangeMaxPrice={handleMaxPrice}
            onChangeStartPrice={handleStartPrice}
          />
        )}
        {step === 2 && (
          <AutoStrategyTimeframeSettings
            timeTerm={timeTerm}
            onChange={setTimeTerm}
            showTimeframe={setShowTimeFrame}
            showAdvanced={setShowAdvanced}
          />
        )}
        {step === 3 && (
          <AutoStrategyInvestmentSettings
            timeTerm={timeTerm}
            timeFrames={timeFrames}
            strategy={strategy}
            middlePrice={middlePrice}
            onChangeLeverage={handleLeverage}
            onChangeInvestment={handleInvestment}
            onChangeCurrency={handleCurrencyType}
            onChangeStepsCount={handleStepsCount}
          />
        )}
      </Box>
      <Spacer height="95px" />

      {showTimeFrame && (
        <AutoStrategyTimeframe
          totalPercent={totalPercent}
          onClose={setShowTimeFrame}
          timeframes={timeFrames}
          onChange={setTimeFrames}
        />
      )}
      {showAdvanced && (
        <AdvancedSettingsStrategy
          onClose={setShowAdvanced}
          value={strategy.positionBehaviour}
          onChange={handleBehaviour}
        />
      )}

      {showPreview && (
        <AutoStrategyPreviewPage
          showPreview={setShowPreview}
          strategy={strategy}
          middlePrice={middlePrice}
          timeFrames={timeFrames}
          term={timeTerm}
          user={user}
          chartOptions={ChartOptions}
        />
      )}
    </PageLayout>
  );
};

export default AutoStrategyCreationPage;
